import { formatDate, formatPrice } from '@/lib/utils.js';
import { renderOfferBlock } from './offer-renderer.js';
import { calculateBookingTotals } from '@/lib/pricing.js';
import { getUiStaffLabel } from '@/lib/ui-labels.js';

export const getBookingSummaryStyles = () => `
  .booking-summary-dialog .booking-summary-button {
    padding: var(--space-sm) var(--space-lg);
    border-radius: var(--radius-md);
    border: none;
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    font-size: var(--font-size-sm);
    background-color: var(--color-primary);
    color: var(--color-text-inverse);
    transition: background-color var(--transition-fast);
  }

  .booking-summary-dialog .booking-summary-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .booking-summary-dialog .booking-summary-button:not(:disabled):hover {
    background-color: var(--color-primary-dark);
  }

  .booking-summary-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
  }

  .booking-summary-dialog {
    width: 100%;
    max-width: 480px;
    background-color: var(--color-surface);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    padding: var(--space-lg);
    margin: var(--space-lg);
  }

  @media (max-width: 640px) {
    .booking-summary-dialog {
      padding: var(--space-md);
      margin: var(--space-sm);
      max-width: calc(100vw - var(--space-md));
    }
  }

  .booking-summary-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-md);
    font-weight: var(--font-weight-semibold);
  }

  .booking-summary-dialog-header span {
    font-size: var(--font-size-lg);
    color: var(--color-primary);
  }

  @media (max-width: 640px) {
    .booking-summary-dialog-header span {
      font-size: var(--font-size-md);
    }
  }

  .booking-summary-dialog-close {
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: var(--radius-full);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--color-border);
    background: var(--color-surface-elevated, var(--color-bg));
    cursor: pointer;
    font-size: 1rem;
    line-height: 1;
    color: var(--color-text-muted);
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .booking-summary-dialog-close:hover {
    background-color: var(--color-surface);
    border-color: var(--color-primary);
    color: var(--color-text);
  }

  .booking-summary-dialog-close:active {
    transform: scale(0.96);
  }

  .booking-summary-dialog-close:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }

  .booking-summary-dialog-body {
    max-height: 320px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
  }

  .booking-summary-item {
    padding: var(--space-sm) 0;
    border-bottom: 1px solid var(--color-border-subtle, var(--color-border));
  }

  .booking-summary-item:last-child {
    border-bottom: none;
  }

  .booking-summary-item-name {
    font-weight: var(--font-weight-medium);
    word-break: break-word;
  }

  @media (max-width: 640px) {
    .booking-summary-item-name {
      font-size: var(--font-size-sm);
    }
  }

  .booking-summary-item-meta {
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    word-break: break-word;
  }

  @media (max-width: 640px) {
    .booking-summary-item-meta {
      font-size: var(--font-size-xs);
    }
  }

  .booking-summary-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-sm);
  }

  .booking-summary-remove {
    border: none;
    background: transparent;
    color: var(--color-text-muted);
    cursor: pointer;
    font-size: var(--font-size-sm);
  }

  .booking-summary-remove:hover {
    color: var(--color-error);
  }

  .booking-summary-footer {
    margin-top: var(--space-lg);
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
    border-top: 1px solid var(--color-border);
    padding-top: var(--space-md);
  }

  .booking-summary-totals {
    font-size: var(--font-size-sm);
    color: var(--color-text);
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);
  }

  @media (max-width: 640px) {
    .booking-summary-totals {
      font-size: var(--font-size-xs);
    }
  }

  .booking-summary-duration-row {
    display: flex;
    align-items: center;
    gap: var(--space-xs);
  }

  .booking-summary-duration-row .offer-activated-badge {
    margin-left: auto;
  }

  .booking-summary-offer {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    color: var(--color-primary-dark);
    display: flex;
    flex-direction: column;
    gap: var(--space-2xs, 0.125rem);
  }

  .offer-block {
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--space-sm);
    background: var(--color-surface-elevated, var(--color-surface));
    display: flex;
    flex-direction: column;
    gap: var(--space-2xs, 0.125rem);
  }

  .offer-block-title {
    font-weight: var(--font-weight-semibold);
    color: var(--color-primary);
    text-decoration-line: underline;
  }

  .offer-block-message {
    font-weight: var(--font-weight-medium);
  }

  .offer-block-details,
  .offer-block-dates {
    color: var(--color-text-muted);
    font-size: var(--font-size-xs);
  }

  .offer-activated-badge {
    align-self: flex-start;
    background: var(--color-success);
    color: var(--color-text-inverse);
    border-radius: 999px;
    padding: 4px 10px;
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-semibold);
    letter-spacing: 0.02em;
    box-shadow: var(--shadow-sm);
    animation: offer-badge-pop 520ms cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes offer-badge-pop {
    0% { transform: scale(0.6); opacity: 0; }
    70% { transform: scale(1.08); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    .offer-activated-badge {
      animation: none;
    }
  }

  .booking-summary-dialog .booking-summary-secondary-button {
    padding: var(--space-sm) var(--space-lg);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-primary);
    cursor: pointer;
    font-weight: var(--font-weight-medium);
    background-color: var(--color-surface);
    color: var(--color-text);
    transition: background-color var(--transition-fast);
  }

  .booking-summary-dialog .booking-summary-secondary-button:hover {
    background-color: var(--color-surface-elevated);
  }
`;

export const getOfferSummaryMessage = ({
  remaining,
  categorieLabel = '',
  loading = false,
  error = null,
} = {}) => {
  if (loading) return "Chargement du compteur d'offre...";
  if (error) return "Compteur d'offre indisponible.";
  if (!Number.isFinite(remaining) || remaining === 0) return '';
  const plural = remaining > 1 ? 'prestations' : 'prestation';
  const label = categorieLabel ? ` ${categorieLabel}` : '';
  return `Plus que ${remaining} ${plural}${label} avant de beneficier de l'offre.`;
};

export const buildBookingSummaryData = ({
  prestations = [],
  offeredIds = new Set(),
  tarifs = null,
  snapshot = null,
  showEmployee = false,
  showCreneau = false,
  offerInfo = null,
  offerMessage = '',
  offerAchieved = false,
  appliedOffers = [],
} = {}) => {
  const totals = calculateBookingTotals({
    prestations,
    offeredIds,
    tarifs,
    appliedOffers
  });

  const items = totals.items.map((item) => {
    const metaParts = [];
    const mins = parseInt(item.duree_min, 10);
    if (Number.isFinite(mins) && mins > 0) metaParts.push(`${mins} min`);
    
    if (item.isOffered) {
      metaParts.push('<span style="color:var(--color-success);font-weight:bold;">Offerte</span>');
    } else if (
      item.isOfferedPublic ||
      item.isOfferedAbonne ||
      item.isDiscounted ||
      item.isTarifPreferentiel
    ) {
      const hasAbonne = Number.isFinite(item.prixFinalAbonne) && item.prixFinalAbonne >= 0;
      const hasPublic = Number.isFinite(item.prixFinalPublic) && item.prixFinalPublic >= 0;

      let discountLabel = '<span style="color:var(--color-primary);font-size:0.9em;">(Offre)</span>';
      if (item.isDiscounted) {
        discountLabel = `(-${item.maxDiscountPercent}%)`;
      } else if (item.isOfferedPublic && !item.isOfferedAbonne) {
        discountLabel = '<span style="color:var(--color-success);font-size:0.9em;">(Offert public)</span>';
      } else if (!item.isOfferedPublic && item.isOfferedAbonne) {
        discountLabel = '<span style="color:var(--color-success);font-size:0.9em;">(Offert abonne)</span>';
      }

      if (hasAbonne && hasPublic) {
        const abonneStr =
          item.prixOriginalAbonne > item.prixFinalAbonne
            ? `<s style="opacity:0.6">${formatPrice(item.prixOriginalAbonne)}</s> <b>${formatPrice(
                item.prixFinalAbonne
              )}</b>`
            : `<b>${formatPrice(item.prixFinalAbonne)}</b>`;

        const publicStr =
          item.prixOriginalPublic > item.prixFinalPublic
            ? `<s style="opacity:0.6">${formatPrice(item.prixOriginalPublic)}</s> <b>${formatPrice(
                item.prixFinalPublic
              )}</b> ${discountLabel}`
            : `<b>${formatPrice(item.prixFinalPublic)}</b> ${discountLabel}`;

        metaParts.push(`Abonné : ${abonneStr}`);
        metaParts.push(`Public : ${publicStr}`);
      } else if (hasPublic) {
        const publicStr =
          item.prixOriginalPublic > item.prixFinalPublic
            ? `<s style="opacity:0.6">${formatPrice(item.prixOriginalPublic)}</s> <b>${formatPrice(
                item.prixFinalPublic
              )}</b> ${discountLabel}`
            : `<b>${formatPrice(item.prixFinalPublic)}</b> ${discountLabel}`;

        metaParts.push(`Tarif : ${publicStr}`);
      } else {
        metaParts.push('Tarif : N/A');
      }
    } else {
      const hasAbonne = Number.isFinite(item.prixFinalAbonne) && item.prixFinalAbonne >= 0;
      const hasPublic = Number.isFinite(item.prixFinalPublic) && item.prixFinalPublic >= 0;

      if (hasAbonne && hasPublic) {
        metaParts.push(`Abonné : ${formatPrice(item.prixFinalAbonne)}`);
        metaParts.push(`Public : ${formatPrice(item.prixFinalPublic)}`);
      } else if (hasPublic) {
        metaParts.push(`Tarif : ${formatPrice(item.prixFinalPublic)}`);
      } else {
        metaParts.push('Tarif : N/A');
      }
    }

    const meta = metaParts.length ? metaParts.join(' / ') : 'Details indisponibles';
    return {
      id: item.id || '',
      name: item.nom || 'Prestation',
      meta,
    };
  });

  const dureeLabel =
    Number.isFinite(totals.totalMinutes) && totals.totalMinutes > 0 ? `${totals.totalMinutes} min` : 'duree inconnue';

  const anyDiscountApplied = totals.globalDiscountApplied;

  const offerBadgeHtml =
    (offerAchieved || anyDiscountApplied) && offerInfo ? '<span class="offer-activated-badge">Offre activée</span>' : '';

  const totalsHtml = `
    <span class="booking-summary-duration-row">
      <span>Durée totale : ${dureeLabel}</span>
      ${offerBadgeHtml}
    </span>
    <span>
      Total Abonné : <s style="opacity:0.6">${typeof totals.totalPrixAbonne === 'number' && totals.totalOriginalPrixAbonne > totals.totalPrixAbonne ? formatPrice(totals.totalOriginalPrixAbonne) : ''}</s> <b>${typeof totals.totalPrixAbonne === 'number' ? formatPrice(totals.totalPrixAbonne) : 'N/A'}</b>
      / Total Public : <s style="opacity:0.6">${typeof totals.totalPrixPublic === 'number' && totals.totalOriginalPrixPublic > totals.totalPrixPublic ? formatPrice(totals.totalOriginalPrixPublic) : ''}</s> <b>${typeof totals.totalPrixPublic === 'number' ? formatPrice(totals.totalPrixPublic) : 'N/A'}</b>
    </span>
    ${
      showEmployee && snapshot?.employe?.nom_complet
        ? `<span>${getUiStaffLabel({ capitalize: true, fallbackSingular: 'Praticien' })} : ${snapshot.employe.nom_complet}</span>`
        : ''
    }
    ${
      showCreneau && snapshot?.creneau?.debut
        ? `<span>Creneau : ${formatDate(snapshot.creneau.debut, 'long')} - ${formatDate(snapshot.creneau.debut, 'time')}</span>`
        : ''
    }
  `;

  const extraHtml = (Array.isArray(appliedOffers) && appliedOffers.length > 0)
    ? appliedOffers.map(off => `
        <div class="booking-summary-offer" style="margin-bottom: var(--space-sm);">
          ${renderOfferBlock(off)}
          ${off.typeOffre === 'n_plus_1' && offerMessage ? `<span>${offerMessage}</span>` : ''}
        </div>
      `).join('')
    : (offerInfo ? `
        <div class="booking-summary-offer">
          ${renderOfferBlock(offerInfo)}
          ${offerMessage ? `<span>${offerMessage}</span>` : ''}
        </div>
      ` : '');

  return { items, totalsHtml, extraHtml };
};

export const renderBookingSummaryOverlay = ({
  isOpen,
  items,
  totalsHtml,
  extraHtml = '',
  title = 'Panier prestations',
  closeAriaLabel = 'Fermer le recapitulatif des prestations',
  continueLabel = 'Continuer la selection',
  bookLabel = 'Prendre rendez-vous',
  showRemove = true,
  showTotals = true,
  showActions = true,
} = {}) => {
  if (!isOpen || !Array.isArray(items) || items.length === 0) return '';

  const totalsBlock = showTotals
    ? `
          <div class="booking-summary-totals">
            ${totalsHtml || ''}
          </div>
    `
    : '';

  const extraBlock = extraHtml || '';

  const actionsBlock = showActions
    ? `
          <div style="display: flex; justify-content: flex-end; gap: var(--space-sm); flex-wrap: wrap;">
            <button
              type="button"
              class="booking-summary-button"
              data-action="cart-book"
              style="flex: 1; min-width: 140px;"
            >
              ${bookLabel}
            </button>
            <button
              type="button"
              class="booking-summary-secondary-button"
              data-action="cart-continue"
              style="flex: 1; min-width: 140px;"
            >
              ${continueLabel}
            </button>
          </div>
    `
    : '';

  const footerContent = [totalsBlock, extraBlock, actionsBlock].filter(Boolean).join('');
  const footerHtml = footerContent ? `<div class="booking-summary-footer">${footerContent}</div>` : '';

  return `
    <div class="booking-summary-overlay" data-overlay-summary>
      <div class="booking-summary-dialog">
        <div class="booking-summary-dialog-header">
          <span>${title}</span>
          <button
            type="button"
            class="booking-summary-dialog-close"
            data-action="close-summary"
            aria-label="${closeAriaLabel}"
          >
            &times;
          </button>
        </div>
        <div class="booking-summary-dialog-body">
          ${items
            .map(
              (item) => `
            <div class="booking-summary-item">
              <div class="booking-summary-item-header">
                <div class="booking-summary-item-name">${item?.name || ''}</div>
                ${
                  showRemove
                    ? `
                <button
                  type="button"
                  class="booking-summary-remove"
                  data-action="cart-remove"
                  data-prestation-id="${item?.id || ''}"
                  aria-label="Supprimer cette prestation du panier"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
                    `
                    : ''
                }
              </div>
              <div class="booking-summary-item-meta">${item?.meta || ''}</div>
            </div>
          `
            )
            .join('')}
        </div>
        ${footerHtml}
      </div>
    </div>
  `;
};
